'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import { isNsfw, isPolitics, isTroll } from '@shared/all/domains/posts';
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTimeShort';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useDatabase from '@shared/utils/hooks/useDatabase';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Lock, PlusCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import PostItemBody from '../../posts/PostItemBody';
import PostSearch from './PostSearch';
type Props = {
  tag: FactiiiTag;
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: (myPostFactiiiCounts: FactiiiTag[]) => void;
  isPrivatePost?: boolean;
};
export const PostReferenceFactiii: React.FC<Props> = t0 => {
  const $ = _c(48);
  const {
    tag,
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    isPrivatePost
  } = t0;
  const {
    fetchPrivatePost
  } = useDatabase();
  const firstPostFactiii = tag.postFactiiis?.[0];
  let t1;
  if ($[0] !== firstPostFactiii?.references) {
    t1 = firstPostFactiii?.references?.map(_temp);
    $[0] = firstPostFactiii?.references;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== tag.factiiiId) {
    t2 = ["PostReferenceFactiii", tag.factiiiId, t1];
    $[2] = t1;
    $[3] = tag.factiiiId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== fetchPrivatePost || $[6] !== firstPostFactiii?.references) {
    t3 = async () => {
      const refs = firstPostFactiii?.references;
      if (!refs?.length) {
        return [];
      }
      const privateRefs = refs.filter(_temp2);
      const serverRefs = refs.filter(_temp3);
      const results = [];
      if (serverRefs.length > 0) {
        const serverPosts = (await unwrapFromResult(client.posts.getMultiple.query({
          postIds: serverRefs.map(_temp4)
        }))).posts;
        results.push(...serverPosts);
      }
      for (const ref of privateRefs) {
        const post = await fetchPrivatePost(ref.uuid.slice(8));
        if (post) {
          results.push(post);
        }
      }
      return results;
    };
    $[5] = fetchPrivatePost;
    $[6] = firstPostFactiii?.references;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data
  } = useQuery(t4);
  const [postsExpanded, setPostsExpanded] = useState(false);
  const [addPostModalOpened, setAddPostModalOpened] = useState(false);
  let t5;
  if ($[11] !== firstPostFactiii?.references || $[12] !== myPostFactiiiCounts || $[13] !== setMyPostFactiiiCounts || $[14] !== tag.factiiiId) {
    t5 = (postId, isPrivate) => {
      const uuid = isPrivate ? `private_${postId}` : postId;
      if (firstPostFactiii?.references?.some(r_3 => r_3.uuid === uuid)) {
        return toast("Post already added", "", "error");
      }
      setMyPostFactiiiCounts(myPostFactiiiCounts.map(f => f.factiiiId === tag.factiiiId ? {
        ...f,
        postFactiiis: [{
          ...f.postFactiiis[0],
          references: [...f.postFactiiis[0].references, {
            uuid
          }]
        }]
      } : f));
    };
    $[11] = firstPostFactiii?.references;
    $[12] = myPostFactiiiCounts;
    $[13] = setMyPostFactiiiCounts;
    $[14] = tag.factiiiId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const addHandler = t5;
  let t6;
  if ($[16] !== firstPostFactiii || $[17] !== myPostFactiiiCounts || $[18] !== setMyPostFactiiiCounts || $[19] !== tag) {
    t6 = postId_0 => {
      const references = firstPostFactiii?.references?.filter(r_4 => r_4.uuid !== postId_0);
      const updatedTag = {
        ...tag,
        postFactiiis: [{
          ...firstPostFactiii,
          references
        }, ...tag.postFactiiis.slice(1)]
      };
      setMyPostFactiiiCounts(myPostFactiiiCounts.map(f_0 => f_0.factiiiId === tag.factiiiId ? updatedTag : f_0));
    };
    $[16] = firstPostFactiii;
    $[17] = myPostFactiiiCounts;
    $[18] = setMyPostFactiiiCounts;
    $[19] = tag;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const removeReference = t6;
  const t7 = `flex flex-row items-center rounded-md border-2 border-ds-border-standard p-4 ${postsExpanded ? " rounded-b-none" : ""} `;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setPostsExpanded(_temp6);
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== postsExpanded) {
    t9 = <button className="mr-2 p-1 hover:text-primary" onClick={t8}>{postsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>;
    $[22] = postsExpanded;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = firstPostFactiii?.references?.length || 0;
  let t11;
  if ($[24] !== t10) {
    t11 = <p>{t10} Referenced Posts</p>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setAddPostModalOpened(true);
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div onClick={t12} className="ml-auto"><PlusCircle /></div>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t7 || $[30] !== t9) {
    t14 = <div className={t7}>{t9}{t11}{t13}</div>;
    $[28] = t11;
    $[29] = t7;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== data || $[33] !== myPostFactiiiCounts || $[34] !== postsExpanded || $[35] !== removeReference) {
    t15 = postsExpanded && <div className="space-y-5 rounded-md rounded-t-none border-2 border-t-0 border-ds-border-standard p-2 transition-all duration-300 ease-in-out">{!data || data.length === 0 ? <p className="text-center text-sm text-textSecondary/70">No posts referenced</p> : <>{data.map(post_0 => <div key={post_0.id} className="mt-2 rounded-md border border-ds-border-standard p-3"><div className="flex flex-row space-x-2"><Avatar avatarKey={post_0.user.avatarKey} robohash={post_0.user.robohash} isBot={post_0.user.tag === "BOT"} userId={post_0.user.id} isAnonymous={post_0.user.anonymous} size={35} /><div className="flex flex-col"><div className="flex items-center gap-x-2">{window.innerWidth < 600 || !post_0.user.name ? <span className="block max-w-[150px] truncate md:max-w-[200px]">@{post_0.user.username}</span> : <p className="mr-1 block max-w-[200px] truncate font-semibold md:max-w-[250px]">{post_0.user.name}</p>}</div><div className="flex gap-x-2 text-textSecondary/70">{window.innerWidth > 600 && post_0.user.name && <><span className="block max-w-[100px] truncate md:max-w-[200px]">@{post_0.user.username}</span><span>•</span></>}<div className="gap-x-2"><div>{formatRelativeTime(new Date(post_0.createdAt))}</div>{post_0.isPrivate && <Lock className="text-textSecondary/70" />}</div></div></div><div onClick={() => removeReference(post_0.id)} className="flex flex-1 items-center justify-end"><XCircle color="red" /></div></div><PostItemBody post={post_0} politics={isPolitics(post_0.postFactiiisCount, post_0.space)} nsfw={isNsfw(post_0.postFactiiisCount)} isFullLength={false} disableLink={true} troll={isTroll(post_0.postFactiiisCount)} isInView={true} ignorePostFactiiiIds={myPostFactiiiCounts.map(_temp7)} /></div>)}</>}</div>;
    $[32] = data;
    $[33] = myPostFactiiiCounts;
    $[34] = postsExpanded;
    $[35] = removeReference;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15 || $[39] !== tag.factiiiId) {
    t16 = <div onClick={_temp5} key={tag.factiiiId} className="mt-2 rounded-md">{t14}{t15}</div>;
    $[37] = t14;
    $[38] = t15;
    $[39] = tag.factiiiId;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== addHandler || $[42] !== addPostModalOpened || $[43] !== isPrivatePost) {
    t17 = addPostModalOpened && <PostSearch includePrivate={isPrivatePost} callback={(postId_1, isPrivate_0) => {
      if (postId_1) {
        addHandler(postId_1, isPrivate_0);
      }
      return setAddPostModalOpened(false);
    }} />;
    $[41] = addHandler;
    $[42] = addPostModalOpened;
    $[43] = isPrivatePost;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t16 || $[46] !== t17) {
    t18 = <>{t16}{t17}</>;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};
function _temp(r) {
  return r.uuid;
}
function _temp2(r_0) {
  return r_0.uuid.startsWith("private_");
}
function _temp3(r_1) {
  return !r_1.uuid.startsWith("private_");
}
function _temp4(r_2) {
  return r_2.uuid;
}
function _temp5(e) {
  return e.stopPropagation();
}
function _temp6(prev) {
  return !prev;
}
function _temp7(f_1) {
  return f_1.id;
}